import { useState, Fragment } from "react";
import { Link } from "react-router-dom";
import { Card, Mono, Badge, Loading } from "../../components/ui";
import { theme } from "../../lib/theme";
import { useTxSlotActivity } from "../../api/hooks";
import { abbreviateHex } from "../../components/TxTable";
import { Collapsible } from "./Collapsible";
import {
  HeatmapBar,
  HeatmapCell,
  NearbyList,
  SlotRow,
  SlotEntry,
  SlotLabelRow,
  WriteBadge,
} from "./styled";

function SlotHeatmap({
  histogram,
  focalBin,
  blockRange,
}: {
  histogram: number[];
  focalBin: number | null;
  blockRange: { min: number; max: number };
}) {
  const maxCount = Math.max(...histogram, 1);
  const [hoveredBin, setHoveredBin] = useState<number | null>(null);
  const range = blockRange.max - blockRange.min || 1;

  return (
    <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
      <HeatmapBar>
        {histogram.map((count, i) => (
          <HeatmapCell
            key={i}
            intensity={count / maxCount}
            isFocal={i === focalBin}
            onMouseEnter={() => setHoveredBin(i)}
            onMouseLeave={() => setHoveredBin(null)}
            title={`Blocks ${(blockRange.min + Math.floor((i / histogram.length) * range)).toLocaleString()}–${(blockRange.min + Math.floor(((i + 1) / histogram.length) * range)).toLocaleString()}: ${count} write${count !== 1 ? "s" : ""}`}
          />
        ))}
      </HeatmapBar>
      {hoveredBin != null && (
        <div
          style={{
            position: "absolute",
            bottom: "calc(100% + 4px)",
            left: `${((hoveredBin + 0.5) / histogram.length) * 100}%`,
            transform: "translateX(-50%)",
            background: theme.colors.bgCard,
            border: `1px solid ${theme.colors.border}`,
            borderRadius: theme.radius.md,
            padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
            fontSize: theme.fontSize.xs,
            whiteSpace: "nowrap",
            zIndex: 10,
            pointerEvents: "none",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
          }}
        >
          <span style={{ color: theme.colors.text }}>
            {histogram[hoveredBin]} write{histogram[hoveredBin] !== 1 ? "s" : ""}
          </span>
          <span style={{ color: theme.colors.textMuted, marginLeft: "6px" }}>
            Blocks {(blockRange.min + Math.floor((hoveredBin / histogram.length) * range)).toLocaleString()}
            –{(blockRange.min + Math.floor(((hoveredBin + 1) / histogram.length) * range)).toLocaleString()}
          </span>
        </div>
      )}
    </div>
  );
}

export function SlotTimelines({
  networkId,
  hash,
}: {
  networkId: string;
  hash: string;
}) {
  const { data, isLoading } = useTxSlotActivity(networkId, hash);
  const [expandedSlot, setExpandedSlot] = useState<string | null>(null);

  if (isLoading) return <Loading />;
  if (!data || data.slots.length === 0) return null;

  return (
    <Collapsible
      title="Public Data Writes"
      count={data.slots.length}
      defaultOpen={false}
    >
      <p
        style={{
          color: theme.colors.textMuted,
          marginBottom: theme.spacing.md,
          fontSize: theme.fontSize.xs,
        }}
      >
        Activity heatmap per storage slot. Brighter = more writes.{" "}
        <span style={{ color: theme.colors.warning, fontWeight: "bold" }}>
          Highlighted
        </span>{" "}
        = this transaction's block. Click a row for nearby transactions.
      </p>

      <Card
        style={{ padding: theme.spacing.md, marginBottom: theme.spacing.md }}
      >
        {data.slots.map((slot) => {
          const slotTitle = slot.resolvedContract
            ? `${slot.resolvedContract.label ?? slot.resolvedContract.address} [${slot.resolvedContract.storageSlotIndex}]`
            : slot.leafSlot;
          const slotContent = slot.resolvedContract ? (
            <>
              <Badge
                color={theme.colors.primary}
                style={{ fontSize: "10px" }}
              >
                {slot.resolvedContract.label ??
                  abbreviateHex(slot.resolvedContract.address)}
              </Badge>{" "}
              <span style={{ color: theme.colors.textMuted }}>
                [{slot.resolvedContract.storageSlotIndex}]
              </span>
            </>
          ) : (
            <Mono style={{ fontSize: "10px" }}>
              {abbreviateHex(slot.leafSlot)}
            </Mono>
          );

          return (
          <SlotEntry key={slot.leafSlot}>
            <SlotLabelRow title={slotTitle}>
              {slotContent}
            </SlotLabelRow>
            <SlotRow
              style={{ cursor: "pointer" }}
              onClick={() =>
                setExpandedSlot(
                  expandedSlot === slot.leafSlot ? null : slot.leafSlot,
                )
              }
            >
              <SlotHeatmap
                histogram={slot.histogram}
                focalBin={slot.focalBin}
                blockRange={slot.blockRange}
              />
              <WriteBadge color={theme.colors.accent}>
                {slot.totalWrites}
              </WriteBadge>
            </SlotRow>
            {expandedSlot === slot.leafSlot &&
              slot.nearbyWrites.length > 0 && (
                <NearbyList>
                  {slot.nearbyWrites.map((w, i) => (
                    <Fragment key={i}>
                      {w.isFocalTx ? (
                        <Badge
                          color={theme.colors.warning}
                          style={{ fontSize: "10px" }}
                        >
                          This tx (Block{" "}
                          {w.blockNumber?.toLocaleString() ?? "?"})
                        </Badge>
                      ) : (
                        <Link
                          to={`/${networkId}/tx/${w.txHash}`}
                          style={{ textDecoration: "none" }}
                        >
                          <Badge
                            color={theme.colors.primary}
                            style={{ fontSize: "10px", cursor: "pointer" }}
                          >
                            {abbreviateHex(w.txHash)} (Block{" "}
                            {w.blockNumber?.toLocaleString() ?? "?"})
                          </Badge>
                        </Link>
                      )}
                    </Fragment>
                  ))}
                </NearbyList>
              )}
          </SlotEntry>
          );
        })}
      </Card>
    </Collapsible>
  );
}
